import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Drawer,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Divider, CircularProgress, Alert, InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiFetch } from '../api';

const SupplierMaster = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    address: '',
    opening_balance: 0,
    status: 'Active'
  });

  const fetchData = async () => {
    setLoading(true);
    try {
        const response = await apiFetch('/api/suppliers');
        const data = await response.json();
        if (Array.isArray(data)) {
            setSuppliers(data);
        } else {
            setError('डेटा लोड करताना त्रुटी आली.');
        }
    } catch (err) {
        setError('डेटा लोड करताना त्रुटी आली.');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDrawerForCreate = () => {
    setEditingId(null);
    setFormData({ name: '', mobile: '', address: '', opening_balance: 0, status: 'Active' });
    setError('');
    setDrawerOpen(true);
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      name: row.name,
      mobile: row.mobile || '',
      address: row.address || '',
      opening_balance: row.opening_balance || 0,
      status: row.status || 'Active'
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('तुम्हाला नक्की हे व्यापारी हटवायचे आहे का?')) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/suppliers/${id}`, { method: 'DELETE' });
      if (response.ok) {
        fetchData();
      } else {
        setError('हटवताना अडचण आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
        setError('कृपया व्यापाऱ्याचे नाव भरा.');
        return;
    }

    setLoading(true);
    const isEditing = !!editingId;
    const endpoint = isEditing ? `/api/suppliers/${editingId}` : '/api/suppliers';
    const method = isEditing ? 'PUT' : 'POST';

    try {
        const response = await apiFetch(endpoint, {
            method,
            body: JSON.stringify(formData)
        });

        if (response.ok) {
            setDrawerOpen(false);
            setEditingId(null);
            setFormData({ name: '', mobile: '', address: '', opening_balance: 0, status: 'Active' });
            fetchData();
        } else {
            setError('माहिती जतन करताना अडचण आली.');
        }
    } catch (err) {
        setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
        setLoading(false);
    }
  };

  const translateStatus = (status) => {
    switch (status) {
      case 'Active': return 'सक्रिय';
      case 'Inactive': return 'निष्क्रिय';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
          व्यापारी मास्टर (Supplier Master)
        </Typography>
        <Button
          variant="contained"
          onClick={openDrawerForCreate}
          sx={{ backgroundColor: '#0FA958', '&:hover': { backgroundColor: '#0C8A47' }, minWidth: 200 }}
        >
          {loading && !drawerOpen ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : '+ नवीन व्यापारी जोडा'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="व्यापाऱ्याचे नाव शोधा..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#627D98' }} />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      {error && !drawerOpen && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 10px rgba(0,0,0,0.05)', maxHeight: '70vh', overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#f0fdf4' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>अ. क्र.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>व्यापाऱ्याचे नाव</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>मोबाईल नंबर</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>पत्ता</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>शिल्लक रक्कम (₹)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>स्थिती</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>अॅक्शन</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && suppliers.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : suppliers
                .filter(row => row.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((row, index) => (
              <TableRow key={row.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell><Typography variant="body1" sx={{ fontWeight: 500 }}>{row.name}</Typography></TableCell>
                <TableCell>{row.mobile}</TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell align="right">₹ {row.current_balance}</TableCell>
                <TableCell align="center">
                  <Box sx={{ 
                      px: 2, py: 0.5, borderRadius: 4, display: 'inline-block',
                      backgroundColor: row.status === 'Active' ? '#e6f4ea' : '#f5f5f5',
                      color: row.status === 'Active' ? '#137333' : '#616161',
                      fontSize: '0.85rem'
                  }}>
                    {translateStatus(row.status)}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#1B5E20' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: '#d32f2f' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
              {editingId ? 'व्यापारी संपादित करा' : 'नवीन व्यापारी जोडा'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          {error && drawerOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField fullWidth label="व्यापाऱ्याचे नाव" variant="outlined" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} sx={{ mb: 2.5 }} />
          <TextField fullWidth label="मोबाईल नंबर" variant="outlined" value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} sx={{ mb: 2.5 }} />
          <TextField fullWidth label="पत्ता" variant="outlined" multiline rows={2} value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} sx={{ mb: 2.5 }} />
          <TextField fullWidth label="आरंभीची शिल्लक (₹)" type="number" variant="outlined" value={formData.opening_balance} onChange={(e) => setFormData({...formData, opening_balance: e.target.value})} sx={{ mb: 2.5 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>स्थिती</InputLabel>
            <Select label="स्थिती" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
              <MenuItem value="Active">सक्रिय</MenuItem>
              <MenuItem value="Inactive">निष्क्रिय</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 'auto', display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setDrawerOpen(false)} color="inherit">रद्द करा</Button>
            <Button fullWidth variant="contained" onClick={handleSave} disabled={loading} sx={{ backgroundColor: '#0FA958' }}> जतन करा </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SupplierMaster;
